import React from 'react';

import { useAppContext } from 'src/components/app-context';
import { usePriceRowContext } from '../../../price-row-context';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';

import { Direction } from '@prisjakt/ui';
import { FontEmphasis, FontWeight } from '@prisjakt/ui/text/variants';
import { BadgeSize } from '@prisjakt/ui/badge';
import { IconLeaf } from '@prisjakt/ui/icon/leaf';
import { Tooltip } from '@prisjakt/ui/tooltip';
import { MobileAndDesktopOnly, TabletOnly } from 'src/ui/block';
import { OfferPriceLabel } from 'src/components/offer/price/offer-price';
import { PriceLabel } from 'src/components/offer/price/price-label';
import { AuthorizedDealerBadge } from '../authorized-dealer';
import { DeliveryTime } from '../delivery-time';
import { PriceTypeNoticeBadge } from './price-type-badge';
import { CountryBadge } from './country-badge';
import { StockBadge } from './stock-badge';

import { getCampaignByOfferId } from '../../../utils';

import { AlternativePrice, ShippingCollection } from 'src/types';
import {
  FilterType,
  SustainabilityEnum,
} from 'src/components/price-list/types';
import { OptionTypeEnum } from 'src/components/filter-dropdown';

import {
  Column,
  MediumUpFlex,
  FirstRow,
  MobileOnlyFlex,
  Row,
  StyledCaptionText,
  StyledSustainabilityBadge,
} from './styles';

interface IStockPriceInfo {
  alternativeOffer?: AlternativePrice;
}

export const StockPriceInfo: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IStockPriceInfo>
>> = props => {
  const { alternativeOffer } = props;
  const { t } = useAppContext();
  const { campaign, isFeatured, offer, sorting } = usePriceRowContext();
  const { getActiveFilters, getPriceDisplay } = useOfferListFilterContext();

  const { isInternationalPrice, store } = offer;

  const getShippingBySort = (shipping?: ShippingCollection | null) => {
    return sorting === OptionTypeEnum.FASTEST_DELIVERY
      ? shipping?.fastest
      : shipping?.cheapest;
  };

  // Get props from alternative offer if supplied
  const id = alternativeOffer ? alternativeOffer.id : offer.priceId;
  const selectedOffer = alternativeOffer || offer;
  const price = alternativeOffer ? alternativeOffer.price : offer.price;
  const offerPrices = alternativeOffer
    ? alternativeOffer.offerPrices
    : offer.offerPrices;
  const shipping = alternativeOffer
    ? getShippingBySort(alternativeOffer.shipping)
    : getShippingBySort(offer.shipping);
  const stock = alternativeOffer ? alternativeOffer.stock : offer.stock;
  const priceAvailability = alternativeOffer
    ? alternativeOffer.availability
    : offer.availability;

  const activeCampaign = getCampaignByOfferId(id, campaign);

  const isDownload = offer.availability?.condition === 'Download';
  const hasSustainableShipping =
    shipping?.sustainability === SustainabilityEnum.SUSTAINABLE;

  const getDeliveryText = () => {
    if (shipping?.shippingCost === 0)
      return (
        <span>
          {shipping?.carrier ? `${shipping?.carrier}, ` : null}
          {t('product', 'price.free-shipping')}
        </span>
      );

    if (!shipping?.shippingCost)
      return (
        <span>
          {shipping?.carrier ? `${shipping?.carrier}, ` : null}
          {t('product', 'price.unknown-shipping')}
        </span>
      );

    // Prefix the shipping cost with carrier or shipping label
    return shipping?.carrier ? (
      <>
        {shipping?.carrier},{' '}
        <PriceLabel value={shipping.shippingCost} weight={FontWeight.NORMAL} />
      </>
    ) : (
      <>
        {t('product', 'delivery-time.label')}{' '}
        <PriceLabel value={shipping.shippingCost} weight={FontWeight.NORMAL} />
      </>
    );
  };

  const deliveryText = getDeliveryText();

  const showDeliveryText = () => {
    if (!deliveryText) return false;

    const filteredOnShippingInfo = Boolean(
      getActiveFilters().find(
        item =>
          item.type === FilterType.PRICES_INC_SHIPPING ||
          item.type === FilterType.DELIVERY_METHOD ||
          item.type === FilterType.CARRIERS
      )
    );

    return (
      sorting === OptionTypeEnum.FASTEST_DELIVERY || filteredOnShippingInfo
    );
  };

  return (
    <Column>
      <FirstRow>
        {!isFeatured && (
          <MediumUpFlex>
            {isInternationalPrice && store?.primaryMarket && (
              <CountryBadge countryCode={store.primaryMarket} />
            )}
          </MediumUpFlex>
        )}
        {price && (
          <>
            {!isFeatured && (
              <StockBadge
                featuredOffer={isFeatured}
                status={stock?.status}
                statusText={stock?.statusText}
              />
            )}
            <OfferPriceLabel
              campaignColors={activeCampaign?.media?.colors}
              container="h4"
              priceDisplay={getPriceDisplay()}
              offer={selectedOffer}
              shipping={shipping ?? undefined}
              offerPrice={offerPrices}
            />
          </>
        )}
      </FirstRow>
      {isFeatured && (
        <>
          {showDeliveryText() ? (
            <Row>
              {hasSustainableShipping && (
                <Tooltip
                  placement={Direction.Up}
                  title={t('product', 'shipping.sustainable')}
                >
                  <StyledSustainabilityBadge
                    circular
                    color="GREEN"
                    icon={IconLeaf}
                    size={BadgeSize.SMALL}
                    $addExtraSpacing={Boolean(offer.authorizedDealer)} // Added to create extra spacing between AD and Sustainable badges in UI
                  />
                </Tooltip>
              )}
              <StyledCaptionText emphasis={FontEmphasis.MEDIUM}>
                {deliveryText}
              </StyledCaptionText>
            </Row>
          ) : null}

          <Row>
            {!alternativeOffer && offer.authorizedDealer && (
              <MobileAndDesktopOnly>
                <AuthorizedDealerBadge />
              </MobileAndDesktopOnly>
            )}
            <MediumUpFlex>
              {priceAvailability && (
                <PriceTypeNoticeBadge priceAvailability={priceAvailability} />
              )}
              {isInternationalPrice && store?.primaryMarket && (
                <CountryBadge countryCode={store.primaryMarket} />
              )}
            </MediumUpFlex>
            <MediumUpFlex>
              {!isDownload && (
                <StockBadge
                  featuredOffer={isFeatured}
                  status={stock?.status}
                  statusText={stock?.statusText}
                />
              )}
            </MediumUpFlex>

            {!isDownload && (
              <MobileOnlyFlex>
                <StockBadge
                  featuredOffer={isFeatured}
                  status={stock?.status}
                  statusText={stock?.statusText}
                />
              </MobileOnlyFlex>
            )}
            {!isDownload && (
              <DeliveryTime
                shipping={shipping}
                stockStatus={stock?.status}
                availability={priceAvailability}
              />
            )}
          </Row>

          {!alternativeOffer && offer.authorizedDealer && (
            <TabletOnly>
              <Row>
                <AuthorizedDealerBadge />
              </Row>
            </TabletOnly>
          )}
        </>
      )}
    </Column>
  );
};
